"use client";
import React, { useState, useEffect, useMemo } from "react";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { REST_API_URL } from "../api/config";

const API_URL = REST_API_URL;
const options = ["Day", "Month"];

const CURRENCY_SYMBOL_MAP = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  AUD: "A$",
  CAD: "C$",
  BRL: "R$",
  MXN: "$",
  AED: "د.إ",
  ARS: "$",
  ZAR: "R",
  ILS: "₪",
};

const getUserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

const buildHeaders = (token) => {
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function IncomeChart() {
  const navigate = useNavigate();

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const [mode, setMode] = useState("Month");
  const [open, setOpen] = useState(false);

  const [dataDay, setDataDay] = useState([]);
  const [dataMonth, setDataMonth] = useState([]);

  const [currencyCode, setCurrencyCode] = useState("USD");
  const [currencySymbol, setCurrencySymbol] = useState("$");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const chartData = mode === "Day" ? dataDay : dataMonth;

  const headers = useMemo(() => {
    if (!token) return null;
    return buildHeaders(token);
  }, [token]);

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token || !headers) return;

    const fetchUserInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/auth/userinfo/`, { headers });
        const code = res.data?.currency || "USD";
        setCurrencyCode(code);
        setCurrencySymbol(CURRENCY_SYMBOL_MAP[code] || code);
      } catch (err) {
        console.error("❌ Failed to fetch user info:", err);
      }
    };

    fetchUserInfo();
  }, [token, headers]);

  useEffect(() => {
    if (!token || !headers) return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(`${API_URL}/client/profile-analytics/`, {
          headers,
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setDataDay(
          (data["By Day"] || []).map((item) => ({
            name: item.label,
            income: item.total || 0,
            isCurrent: item.is_current,
            percentage: item.percentage_change,
          }))
        );

        setDataMonth(
          (data["By Month"] || []).map((item) => ({
            name: item.label,
            income: item.total || 0,
            isCurrent: item.is_current,
            percentage: item.percentage_change,
          }))
        );
      } catch (err) {
        console.error("❌ Failed to load analytics:", err);
        setError("Failed to load analytics data.");
        setDataDay([]);
        setDataMonth([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [token, headers, navigate]);

  const chartHasData = chartData.some((item) => item.income > 0);
  const total = chartData.reduce((sum, i) => sum + (i.income || 0), 0);

  return (
    <div className="rounded-xl bg-white shadow p-6 w-full relative h-full">
      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="font-bold text-2xl text-gray-800">Total income</p>

          {loading ? (
            <p className="mt-4 text-neutral-400">Loading...</p>
          ) : (
            <p className="font-normal text-[30px] mt-4 text-gray-800">
              {currencySymbol}
              {total.toLocaleString()}
            </p>
          )}
        </div>

        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-1 w-[132px] h-10 rounded-[14px] border border-neutral-300 
                       text-sm text-[#9A99AB] bg-white hover:bg-neutral-50 pl-4 pr-2"
          >
            By {mode}
            <ChevronDownIcon
              className={`w-4 h-4 transition ${open ? "rotate-180" : ""}`}
            />
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-32 rounded-lg bg-white shadow-md border z-10">
              {options.map((opt) => (
                <div
                  key={opt}
                  onClick={() => {
                    setMode(opt);
                    setOpen(false);
                  }}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-indigo-50 ${
                    mode === opt
                      ? "text-indigo-600 font-medium"
                      : "text-neutral-700"
                  }`}
                >
                  By {opt}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-red-500 text-sm mb-2 italic">{error}</p>}

      {loading ? (
        <div className="flex justify-center items-center h-[250px] text-neutral-400">
          Loading chart...
        </div>
      ) : chartHasData ? (
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <XAxis dataKey="name" axisLine={false} tickLine={false} />
            <Tooltip
              cursor={{ fill: "transparent" }}
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="bg-indigo-600 text-white px-3 py-1 rounded-md text-sm shadow">
                    {currencySymbol}
                    {payload[0].value}
                    {payload[0].payload.percentage !== null && (
                      <span className="ml-2 text-xs text-gray-200">
                        ({payload[0].payload.percentage > 0 ? "+" : ""}
                        {payload[0].payload.percentage}%)
                      </span>
                    )}
                  </div>
                ) : null
              }
            />
            <Bar
              dataKey="income"
              radius={[20, 20, 20, 20]}
              fill="#E5E7EB"
              barSize={45}
              activeBar={{ fill: "#4B5CFF" }}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-neutral-400 text-center h-[250px] flex items-center justify-center">
          No income data.
        </p>
      )}
    </div>
  );
}